import React, { useEffect, useState } from 'react'
import { Autocomplete, Box, Button, Grid, InputAdornment, TextField, makeStyles, CircularProgress, Typography } from '@material-ui/core';
import SearchIcon from '../../../icons/Search'
import { useNavigate } from 'react-router';
import { useTheme } from '@material-ui/core';
import useSettings from '../../../hooks/useSettings';
import useAuth from '../../../hooks/useAuth';
import useAPI from '../../../hooks/useAPI';
import { FilterBuilder } from '@loopback/filter';
import { createFilterOptions } from '@material-ui/core';
import { THEMES } from '../../../constants';

const useStyles = makeStyles((theme) => ({
  searchInput: {
    backgroundColor: '#fff',
    borderRadius: 5,
  },
  lengthSelect: {
    borderRadius: 5,
  },
  browseButton: {
    borderRadius: 5,
  },
}))

const times = [
  {
    title: ':15',
    value: '15'
  },
  {
    title: ':30',
    value: '30'
  },
  {
    title: ':60',
    value: '60'
  },
]

const Search = (props) => {
  const { categories, handleSearch } = props
  const classes = useStyles()
  const theme = useTheme()
  const { settings } = useSettings()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [category, setCategory] = useState(null)
  const [length, setLength] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSearchTerm, setSelectedSearchTerm] = useState('')
  const [campaigns, setCampaigns] = useState([])
  const { api } = useAPI()
  const [searchTimeout, setSearchTimeout] = useState(null)
  const resultsPerPage = 50

  const handleSearchSubmit = () => {
    navigate(`/search-results?searchTerm=${searchTerm}&length=${length}&category=${category ? category.name.replace('&', 'ampersand').split(' ').join('+') : ''}`)
  }

  useEffect(() => {
    if (category)
      handleSearchSubmit()
  }, [category])

  // if selectedSearchTerm changes automatically navigate to the campaign page by it's id
  useEffect(() => {
    if (selectedSearchTerm) {
      const campaign = campaigns.find(c => c.title === selectedSearchTerm)
      if (campaign)
        navigate(`/spotrequestoptions/${campaign.id}`)
    }
  }, [selectedSearchTerm])

  useEffect(() => {
    if (searchTerm.length < 2) {
      setCampaigns([])
    }

    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }
    const newDelay = setTimeout(async () => {
      if (searchTerm && searchTerm.length > 1) {
        getCampaigns(searchTerm)
        setSearchTimeout(null)

      }
    }, 250)
    setSearchTimeout(newDelay)
  }, [searchTerm])

  // get options for autocomplete from API for campaigns
  const getCampaigns = async (searchTerm) => {
    // If search includes a category, we must query data by category and include the campaigns
    const filter = new FilterBuilder()
    filter.include('media', 'categories')
    filter.order('title ASC')
    filter.where({
      and: [
        { 'isActive': true },
        { 'isDeleted': false },
        {
          or: [
            { title: { like: searchTerm ? `%${searchTerm.toLowerCase()}%` : '%' } },
            { keyWords: { like: searchTerm ? `%${searchTerm.toLowerCase()}%` : '%' } },
            { description: { like: searchTerm ? `%${searchTerm.toLowerCase()}%` : '%' } },
          ]
        }
      ]
    })
    filter.limit(resultsPerPage)
    const result = await api.Campaign.find(filter.build())
    if (result.error) {
      console.error("Failed to retrieve campaigns:", result)
      return
    }

    setCampaigns(result)
  }

  const filterOptions = createFilterOptions({
    matchFrom: 'any',
    stringify: (option) => option.title + ' ' + option.keyWords + ' ' + option.description,
  });

  return (
    <Grid container spacing={2}>
      <Grid item md={5} sm={12} xs={12}>
        <Autocomplete
          className={classes.searchInput}
          freeSolo
          autoComplete
          getOptionLabel={(option) => option.title}
          filterOptions={filterOptions}
          options={campaigns}
          open={campaigns.length > 0}
          isOptionEqualToValue={(option, value) => {
            return option.title == value
          }}
          size="small"
          onChange={(e, value) => { console.log('selected keyword term:' + value); setSelectedSearchTerm(value.title) }}
          onBlur={() => { console.log('blur'); setCampaigns([]) }}
          renderInput={(params) => (<TextField
            fullWidth
            placeholder="Search by Keyword"
            variant="outlined"
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit() }}
            size="small"
            InputProps={{
              endAdornment:
                <>
                  {searchTimeout ? <CircularProgress color="inherit" size={20} /> : <InputAdornment position="end"><SearchIcon /></InputAdornment>}
                </>
            }}
            {...params}
          />)}
        />
      </Grid>
      <Grid item md={5} sm={12} xs={12}>
        <Box>
          <Autocomplete
            getOptionLabel={(option) => option.name}
            options={categories}
            autoComplete
            size="small"
            onChange={(e, value) => { console.log(value); setCategory(value) }}
            value={category}
            renderInput={(params) => (
              <TextField
                className={classes.browseButton}
                fullWidth
                name="category"
                InputLabelProps={{ shrink: false }}
                placeholder="Browse Category"
                variant="outlined"
                {...params}
                sx={{ backgroundColor: 'background.paper' }}
              />
            )}
          />
        </Box>
      </Grid>
      <Grid item md={2} sm={12} xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', margin: 'auto' }}>
          <Button
            variant="contained"
            sx={{ backgroundColor: theme.palette.primary.main, px: 6, py: 1 }}
            onClick={handleSearchSubmit}
            onChange={handleSearch}
          >
            Search
          </Button>
        </Box>
      </Grid>
      <Grid item md={12} sm={12} xs={12} sx={{ mt: -2 }}>
        <Typography variant="body2" ml={0} mt={1} style={{ color: 'white' }}>
          Browse our spec library, and customize for your client.
        </Typography>
        {(settings.theme == 'SPECBYTE' || settings.theme == THEMES.CASHBYCREATIVE) &&
          <Button
            sx={{ ml: -2 }}
            onClick={() => navigate('/concierge/scriptai')}
          >
            <Typography variant="body2" mx={1} style={{ color: 'white' }}>
              Need a custom script? Try our AI script writer.
            </Typography>
          </Button>
        }
      </Grid>
      {
        settings.theme == 'SPECBYTE' &&
        <Box sx={{ position: 'relative', top: '3rem', display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', margin: 'auto', justifyContent: 'center' }}>
            <Button
              variant="contained"
              sx={{
                background: `linear-gradient(to right, ${theme.palette.primary.dark} 0 0%, ${theme.palette.primary.main} 125% 100%)`,
                px: 10,
                py: 2.5,
                textTransform: 'uppercase',
                fontWeight: 800,
              }}
              onClick={() => navigate('/concierge/7')}
            >
              OTT, PreRoll Video & Digital Banners - Click Here!
            </Button>
          </Box>
        </Box>
      }
    </Grid>
  )
};

export default Search;
